// Module 11 — dashboards public surface (FROZEN dashboards.v1): mountDashboards(root) → unmount.
// This is the ONLY file that value-imports uplot, touches the global `document`, and imports the
// bundled scenarios.json — bound to the structural seams via localized `as unknown as` casts so the
// rest of the module (and its tests) never load the DOM/canvas modules. Deps: domain + state + npm.
import uPlot from "uplot";
import { sim } from "../state/index";
import scenariosJson from "../../data/scenarios.json";
import { makeDashboards } from "./mount";
import type { InspectorModel } from "./inspector";
import type { UPlotCtor, UiDocument, UiElement, ScenarioCatalog, UiStorage } from "./seam";

export type { InspectorModel } from "./inspector";

// localStorage wrapped so private-mode / quota errors never break the workspace (degrades to no-persist).
function safeStorage(): UiStorage | undefined {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return undefined;
    return {
      getItem: (k) => { try { return ls.getItem(k); } catch { return null; } },
      setItem: (k, v) => { try { ls.setItem(k, v); } catch { /* ignore */ } },
    };
  } catch {
    return undefined;
  }
}

function baseDeps() {
  const storage = safeStorage();
  return {
    store: sim,
    uplot: uPlot as unknown as UPlotCtor,
    doc: document as unknown as UiDocument,
    catalog: scenariosJson as unknown as ScenarioCatalog,
    ...(storage ? { storage } : {}),
  };
}

export function mountDashboards(root: HTMLElement): () => void {
  return makeDashboards(baseDeps())(root as unknown as UiElement);
}

// ADDITIVE (non-contract, like makeDashboards itself — see the dashboards.v1 freeze note): the
// composition root threads the STATIC reduced model in, unlocking the full object inspector —
// every v3 attribute of the picked node/plant/neighbourhood/cable + its live per-object graph.
// `mobileTabMode` (P-MOBILE): true on a detected phone — see DashboardsDeps.mobileTabMode.
// `onWorkspaceReady` (P-DESKTOP-FLOAT): see DashboardsDeps.onWorkspaceReady — lets app/index.ts capture
// a narrow drag-handle onto the live workspace without widening compose.ts's mountDashboards contract.
export function mountDashboardsWithModel(
  model: InspectorModel,
  voltageOverlayCallback?: (on: boolean) => void,
  mobileTabMode?: boolean,
  onWorkspaceReady?: (handle: { setPanelPosition(id: string, x: number, y: number): void; bringToFront(id: string): void }) => void,
): (root: HTMLElement) => () => void {
  const mounted = makeDashboards({
    ...baseDeps(), model,
    ...(voltageOverlayCallback ? { voltageOverlayCallback } : {}),
    ...(mobileTabMode ? { mobileTabMode } : {}),
    ...(onWorkspaceReady ? { onWorkspaceReady } : {}),
  });
  return (root) => mounted(root as unknown as UiElement);
}
